import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * `icon` es una clase CSS global (p. ej. `dxf_flag_mx`), no una ruta. `iconUrl`
 * es para banderas que llegan del backend como URL (catálogo de códigos de país).
 */
export type AuthSelectOption = {
  value: string;
  label: string;
  icon?: string;
  iconUrl?: string;
  /** Etiqueta corta para el disparador cuando la de la lista no cabe (p. ej. `+52`). */
  triggerLabel?: string;
};

/**
 * `field` es el control de formulario (blanco, alto de input). `pill` es la
 * píldora translúcida que va sobre el video de las pantallas de auth.
 */
export type AuthSelectVariant = 'field' | 'pill';

/**
 * Dropdown propio para las pantallas de autenticación, en reemplazo de los
 * `<select>` nativos. El popup nativo lo dibuja el navegador fuera de la página
 * y no respeta la escala del viewport (en móvil salía diminuto y descolocado);
 * este panel se renderiza dentro del DOM, así que se ve idéntico en cualquier
 * dispositivo — igual que el `Select` custom del mock de dexcarga-2.0.
 *
 * Funciona con formularios reactivos (`formControlName`) vía CVA y también
 * standalone con `[(value)]`.
 */
@Component({
  standalone: true,
  selector: 'dxc-auth-select',
  templateUrl: './auth-select.component.html',
  styleUrls: ['./auth-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AuthSelectComponent), multi: true }]
})
export class AuthSelectComponent implements ControlValueAccessor {

  @Input() options: AuthSelectOption[] = [];
  @Input() placeholder = 'Selecciona...';
  /** Id del botón disparador, para asociarlo al `<label for>` del campo. */
  @Input() selectId = '';
  @Input() invalid = false;
  @Input() variant: AuthSelectVariant = 'field';
  /** El panel se alinea al borde opuesto cuando el control vive pegado a la derecha. */
  @Input() align: 'left' | 'right' = 'left';
  @Input() ariaLabel = '';
  /** Filtro dentro del panel. Necesario en listas largas como el catálogo de países. */
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Buscar...';
  @Input() emptyLabel = 'Sin resultados';

  @HostBinding('class.auth-select--pill') get isPill() { return this.variant === 'pill'; }
  @HostBinding('class.auth-select--right') get isRightAligned() { return this.align === 'right'; }
  @HostBinding('class.auth-select--searchable') get isSearchable() { return this.searchable; }

  @Input() set value(value: string) { this.#value = value ?? ''; }
  get value() { return this.#value; }
  @Output() valueChange = new EventEmitter<string>();

  #value = '';
  disabled = false;
  open = false;
  activeIndex = -1;
  query = '';

  #onChange: (value: string) => void = () => { };
  #onTouched: () => void = () => { };

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) { }

  get selected() { return this.options.find(option => option.value === this.value); }
  get selectedLabel() { return this.selected?.triggerLabel ?? this.selected?.label ?? ''; }

  /** Lo que se pinta y lo que recorre el teclado: con filtro activo son las coincidencias. */
  get visibleOptions() {
    const query = this.query.trim().toLowerCase();
    if (!this.searchable || !query) return this.options;
    return this.options.filter(option =>
      option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query)
    );
  }

  onSearch(query: string) {
    this.query = query;
    this.activeIndex = this.visibleOptions.length ? 0 : -1;
  }

  // --- ControlValueAccessor --------------------------------------------------

  writeValue(value: string) { this.#value = value ?? ''; this.cdr.markForCheck(); }
  registerOnChange(fn: (value: string) => void) { this.#onChange = fn; }
  registerOnTouched(fn: () => void) { this.#onTouched = fn; }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    if (disabled) this.open = false;
    this.cdr.markForCheck();
  }

  // --- Interacción -----------------------------------------------------------

  toggle() {
    if (this.disabled) return;
    this.open = !this.open;
    // El filtro no sobrevive al cierre: reabrir siempre muestra el catálogo entero.
    this.query = '';
    if (this.open) this.activeIndex = Math.max(0, this.options.findIndex(option => option.value === this.value));
    else this.#onTouched();
  }

  select(option: AuthSelectOption) {
    this.#value = option.value;
    this.#onChange(option.value);
    this.valueChange.emit(option.value);
    this.close();
  }

  close() {
    if (!this.open) return;
    this.open = false;
    this.query = '';
    this.#onTouched();
    this.cdr.markForCheck();
  }

  onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'Tab':
        this.close();
        return; // sin preventDefault: el foco debe seguir su curso
      case 'Enter':
      case ' ': {
        // Con buscador abierto, el espacio pertenece al texto que se escribe.
        if (this.open && this.searchable && event.key === ' ') return;
        const options = this.visibleOptions;
        this.open && this.activeIndex >= 0 && options[this.activeIndex]
          ? this.select(options[this.activeIndex])
          : this.toggle();
        break;
      }
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this.open) { this.toggle(); break; }
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = Math.min(this.visibleOptions.length - 1, Math.max(0, this.activeIndex + delta));
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.close();
  }
}
